import { accessOf, type Access } from './plans';

/**
 * Regulile de aprobare a comenzilor. Un pas spune „comenzile de peste X lei le
 * aprobă cineva”; o comandă trece prin toți pașii al căror prag îl depășește,
 * de la cel mai mic la cel mai mare (peste 10.000 lei directorul de achiziții,
 * peste 50.000 lei și CFO-ul).
 *
 * Ce poate face fiecare pachet:
 * - Start: fără aprobări;
 * - Echipă (și proba): un singur pas, aprobat de orice aprobator sau de un om anume;
 * - Companie (și firmele de dinainte de pachete): mai mulți pași și aprobator
 *   pe șantier.
 */

export type ApprovalLevel = 'none' | 'single' | 'multi';

/** Cine aprobă un pas: orice aprobator, aprobatorul șantierului sau un om anume (id). */
export type StepApprover = 'any' | 'site' | (string & {});

export interface ApprovalStepRule {
  /** lei fără TVA: pasul se aplică comenzilor peste această valoare */
  minValue: number;
  approver: StepApprover;
}

export interface ApprovalSettings {
  level: ApprovalLevel;
  steps: ApprovalStepRule[];
  /** aprobatorii activi ai firmei, pentru alegere */
  approvers: { id: string; name: string }[];
  /** șantierele deschise, cu aprobatorul lor (doar la `multi`) */
  sites: { id: string; name: string; approverId: string | null }[];
}

export const MAX_STEPS = 5;

export function approvalLevel(access: Access): ApprovalLevel {
  if (access.state === 'legacy') return 'multi';
  return access.plan?.approvals ?? 'none';
}

export const approvalLevelOf = (c: { plan: string | null; trialEndsAt: string | null }) =>
  approvalLevel(accessOf(c));

/** Pașii pe care îi poate folosi pachetul: la `single`, doar primul. */
export function usableSteps(steps: ApprovalStepRule[], level: ApprovalLevel): ApprovalStepRule[] {
  const sorted = [...steps].sort((a, b) => a.minValue - b.minValue);
  if (level === 'none') return [];
  if (level === 'single') return sorted.slice(0, 1).map((s) => (s.approver === 'site' ? { ...s, approver: 'any' } : s));
  return sorted;
}

export interface ChainStep {
  minValue: number;
  /** cine trebuie să aprobe; null = oricare aprobator al firmei */
  approverId: string | null;
}

/**
 * Pașii prin care trece o comandă de `value` lei, în ordine. Gol = se emite
 * direct. „Aprobatorul șantierului” fără nimeni pus pe șantier devine „oricare”.
 */
export function approvalChain(
  value: number,
  steps: ApprovalStepRule[],
  level: ApprovalLevel,
  siteApproverId: string | null,
): ChainStep[] {
  return usableSteps(steps, level)
    .filter((s) => value > s.minValue)
    .map((s) => ({
      minValue: s.minValue,
      approverId: s.approver === 'any' ? null : s.approver === 'site' ? siteApproverId : s.approver,
    }));
}

export const NO_APPROVALS_MESSAGE =
  'Aprobările comenzilor sunt incluse din pachetul Echipă. Alege un pachet ca să le pornești.';
